import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker, Button, Spin, Empty, Progress } from 'antd';
import { Wallet, IndianRupee, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import dayjs from 'dayjs';
import useApiMethods from '../../lib/useApiMethods';
import { BUDGET_OVERVIEW, CASH_COLLECTED_SUMMARY } from '../../constants/api';

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatLakhs = (amount) => {
  const n = Number(amount) || 0;
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return formatINR(n);
};

const OverviewTab = () => {
  const { fetch } = useApiMethods();

  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [budgets, setBudgets] = useState([]);
  const [budgetsLoading, setBudgetsLoading] = useState(true);
  const [cash, setCash] = useState(null);
  const [cashLoading, setCashLoading] = useState(true);

  const monthDate = useMemo(() => selectedMonth.format('YYYY-MM-DD'), [selectedMonth]);
  const monthKey = useMemo(() => selectedMonth.format('YYYY-MM'), [selectedMonth]);

  const loadBudgets = useCallback(async () => {
    const res = await fetch(BUDGET_OVERVIEW, { month: monthDate }, setBudgetsLoading);
    if (res?.status === 'success') {
      setBudgets(Array.isArray(res.data) ? res.data : []);
    } else {
      setBudgets([]);
    }
  }, [monthDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCash = useCallback(async () => {
    const res = await fetch(CASH_COLLECTED_SUMMARY, { month: monthKey }, setCashLoading);
    if (res?.status === 'success') setCash(res.data);
  }, [monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadBudgets();
    loadCash();
  }, [loadBudgets, loadCash]);

  // ── Aggregate budgets ──
  const totals = useMemo(() => {
    let allocated = 0;
    let spent = 0;
    budgets.forEach((b) => {
      allocated += Number(b.allocated) || 0;
      spent += Number(b.spent) || 0;
    });
    const remaining = allocated - spent;
    const percent = allocated > 0 ? Math.round((spent / allocated) * 100) : 0;
    return { allocated, spent, remaining, percent };
  }, [budgets]);

  const cashTotal = Number(cash?.grand_total) || 0;
  const netPosition = cashTotal - totals.spent;

  const utilStatus = totals.percent > 90 ? 'red' : totals.percent >= 70 ? 'amber' : 'green';
  const utilColor = {
    red: { bg: 'bg-rose-500', stroke: '#ef4444', text: 'text-rose-600 dark:text-rose-400' },
    amber: { bg: 'bg-amber-500', stroke: '#f59e0b', text: 'text-amber-600 dark:text-amber-400' },
    green: { bg: 'bg-emerald-500', stroke: '#10b981', text: 'text-emerald-600 dark:text-emerald-400' },
  }[utilStatus];

  const topDepts = useMemo(() => {
    return [...budgets]
      .sort((a, b) => (Number(b.spent) || 0) - (Number(a.spent) || 0))
      .slice(0, 5);
  }, [budgets]);

  const monthLabel = selectedMonth.format('MMMM YYYY');
  const loading = budgetsLoading || cashLoading;
  const isCurrentMonth = selectedMonth.isSame(dayjs(), 'month');

  return (
    <div className="space-y-6">
      {/* Header with month picker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Finance Overview</h3>
          <p className="text-xs text-muted-foreground">
            Budget, spend, and cash collected — {monthLabel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Month</span>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(v) => v && setSelectedMonth(v.startOf('month'))}
            format="MMM YYYY"
            allowClear={false}
          />
          {!isCurrentMonth && (
            <Button size="small" onClick={() => setSelectedMonth(dayjs().startOf('month'))}>
              This month
            </Button>
          )}
        </div>
      </div>

      {/* KPI grid */}
      {loading && !cash && budgets.length === 0 ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi
            icon={<Wallet className="w-5 h-5 text-white" />}
            iconBg="bg-violet-500"
            title="Total Budget"
            value={formatLakhs(totals.allocated)}
            subtitle={budgets.length ? `${budgets.length} departments` : 'No budgets for this month'}
          />
          <Kpi
            icon={<FileText className="w-5 h-5 text-white" />}
            iconBg={utilColor.bg}
            title="Spent (Actuals)"
            value={formatLakhs(totals.spent)}
            subtitle={`${totals.percent}% of budget used`}
            extra={
              <Progress
                percent={Math.min(totals.percent, 100)}
                showInfo={false}
                strokeColor={utilColor.stroke}
                size="small"
                className="mt-2"
              />
            }
          />
          <Kpi
            icon={<IndianRupee className="w-5 h-5 text-white" />}
            iconBg="bg-teal-500"
            title="Cash Collected"
            value={formatLakhs(cashTotal)}
            subtitle={`CRM ${formatLakhs(cash?.crm?.total)} · Manual ${formatLakhs(cash?.manual?.total)}`}
            warning={cash?.crm?.error ? 'CRM unreachable' : null}
          />
          <Kpi
            icon={netPosition >= 0
              ? <TrendingUp className="w-5 h-5 text-white" />
              : <TrendingDown className="w-5 h-5 text-white" />}
            iconBg={netPosition >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}
            title="Net Position"
            value={formatLakhs(netPosition)}
            subtitle="Cash collected − spent"
            highlight={netPosition >= 0 ? 'positive' : 'negative'}
          />
        </div>
      )}

      {/* Mini sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Budget by Department */}
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground">Spend by Department</h4>
            <span className="text-xs text-muted-foreground">Top {Math.min(topDepts.length, 5)}</span>
          </div>
          {topDepts.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No budget data" />
          ) : (
            <div className="space-y-3">
              {topDepts.map((d) => {
                const pct = Number(d.percent_used) || 0;
                const stroke = pct > 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981';
                return (
                  <div key={d.id}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-foreground truncate">{d.department}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatLakhs(d.spent)} / {formatLakhs(d.allocated)}{' '}
                        <span className="font-semibold" style={{ color: stroke }}>· {pct}%</span>
                      </span>
                    </div>
                    <Progress percent={Math.min(pct, 100)} showInfo={false} strokeColor={stroke} size="small" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cash source breakdown */}
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground">Cash Sources</h4>
            <span className="text-xs text-muted-foreground">
              {(cash?.crm?.receipt_count ?? 0) + (cash?.manual?.entry_count ?? 0)} entries
            </span>
          </div>
          {!cash || cashTotal === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No cash collected" />
          ) : (
            <div className="space-y-3">
              <SourceRow
                label="Product receipts (CRM)"
                amount={cash.crm?.product}
                total={cashTotal}
                colour="#0d9488"
              />
              <SourceRow
                label="AMC receipts (CRM)"
                amount={cash.crm?.amc}
                total={cashTotal}
                colour="#14b8a6"
              />
              <SourceRow
                label="Manual entries (ERP)"
                amount={cash.manual?.total}
                total={cashTotal}
                colour="#6366f1"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ icon, iconBg, title, value, subtitle, extra, highlight, warning }) => (
  <div className={`rounded-2xl border shadow-sm p-5 ${
    highlight === 'positive'
      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-500/40'
      : highlight === 'negative'
      ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-500/40'
      : 'bg-card border-border'
  }`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
    </div>
    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{title}</div>
    <div className={`text-2xl font-bold tabular-nums mb-1 ${
      highlight === 'positive'
        ? 'text-emerald-600 dark:text-emerald-400'
        : highlight === 'negative'
        ? 'text-rose-600 dark:text-rose-400'
        : 'text-foreground'
    }`}>
      {value}
    </div>
    {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
    {extra}
    {warning && <div className="mt-2 text-xs text-amber-600">⚠ {warning}</div>}
  </div>
);

const SourceRow = ({ label, amount, total, colour }) => {
  const n = Number(amount) || 0;
  const pct = total > 0 ? Math.round((n / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-foreground">{label}</span>
        <span className="font-medium tabular-nums">
          {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)}
          <span className="text-xs text-muted-foreground ml-2">({pct}%)</span>
        </span>
      </div>
      <Progress percent={pct} showInfo={false} strokeColor={colour} size="small" />
    </div>
  );
};

export default OverviewTab;
